import { useCookieStoreInfo } from '@ori/personal-beauty-store';
import { useGetTenantMobileAppSmartAppBanner } from '@ori/static-api-client-react';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useMobileOs } from '../../hooks/useMobileOs';
import { useAppConfig } from '../configurationContext';
import type { AppPromoContext } from './types';
import { isAppPromoBannerVisible, setHideAppPromoBannerUntilDate } from './utils';

export const appPromoContext = createContext<AppPromoContext>({});

const { Provider } = appPromoContext;

export const AppPromoProvider: FC<PropsWithChildren> = ({ children }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [pagePath, setPagePath] = useState<string>();
  const { currentLanguage, tenant } = useAppConfig();
  const { owner: store } = useCookieStoreInfo();
  const { userOs } = useMobileOs();
  useLayoutEffect(() => {
    setPagePath(window.location.pathname);
  }, []);
  const { data } = useGetTenantMobileAppSmartAppBanner(
    tenant,
    userOs ?? 'Android',
    {
      lang: currentLanguage,
      pagePath: pagePath ?? '',
      storeName: store ?? undefined,
    },
    {
      swr: {
        enabled: Boolean(userOs && pagePath),
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        shouldRetryOnError: false,
      },
    },
  );
  useEffect(() => {
    setVisible(isAppPromoBannerVisible({ data, userOs }));
  }, [data, userOs]);
  const onClose = useCallback(() => {
    setVisible(false);
    setHideAppPromoBannerUntilDate();
  }, []);
  const value = useMemo<AppPromoContext>(
    () => ({
      appDescription: data?.appDescription,
      appIcon: data?.appIcon,
      appName: data?.appName,
      bannerLink: data?.bannerLink,
      onClose,
      visible,
    }),
    [data?.appDescription, data?.appIcon, data?.appName, data?.bannerLink, onClose, visible],
  );

  return <Provider value={value}>{children}</Provider>;
};

export const useAppPromo = () => useContext(appPromoContext);
